import React, {useEffect, useState} from "react";
import {FaBoxOpen, FaCar, FaClock, FaTools, FaUser} from "react-icons/fa";
import {addJobCardForWorkshopId, getInventoryForWorkshop, getWorkTypeByWorkshopId,} from "../../Api/Api";
import {useWorkshop} from "../Common/WorkshopContext";

const CreateJobCardModal = ({ show, handleClose, customerId }) => {
    const [formData, setFormData] = useState({
        customerId: customerId,
        vehicleNumber: "",
        workTypes: [],
        assignedEmployees: [],
        inventoryItem: [],
        estimatedTime: "",
    });

    const [workTypes, setWorkTypes] = useState([]);
    const [inventoryOptions, setInventoryOptions] = useState([]);
    const [selectedItems, setSelectedItems] = useState({});
    const [loading, setLoading] = useState(false);
    const [workTypesLoading, setWorkTypesLoading] = useState(true);
    const { workshopId } = useWorkshop();

    useEffect(() => {
        if (show) {
            fetchWorkTypes();
            fetchInventory();
        }
    }, [show]);

    const fetchWorkTypes = async () => {
        setWorkTypesLoading(true);
        try {
            const response = await getWorkTypeByWorkshopId(workshopId);
            setWorkTypes(response.data);
        } catch (error) {
            console.error("Error fetching work types:", error);
        } finally {
            setWorkTypesLoading(false);
        }
    };

    const fetchInventory = async () => {
        try {
            const response = await getInventoryForWorkshop(workshopId);
            setInventoryOptions(response.data);
        } catch (err) {
            console.error("Error fetching inventory:", err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleWorkTypeChange = (workTypeId) => {
        setFormData((prevData) => {
            const isSelected = prevData.workTypes.includes(workTypeId);
            const updatedWorkTypes = isSelected
                ? prevData.workTypes.filter((id) => id !== workTypeId)
                : [...prevData.workTypes, workTypeId];
            return { ...prevData, workTypes: updatedWorkTypes };
        });
    };

    const handleSelectChange = (inventoryId, isSelected) => {
        setSelectedItems((prev) => {
            const updatedItems = { ...prev };
            if (isSelected) {
                updatedItems[inventoryId] = { quantity: 1, underWarranty: false };
            } else {
                delete updatedItems[inventoryId];
            }
            return updatedItems;
        });
    };

    const handleQuantityChange = (inventoryId, quantity) => {
        setSelectedItems((prev) => ({
            ...prev,
            [inventoryId]: { ...prev[inventoryId], quantity },
        }));
    };

    const handleWarrantyChange = (inventoryId, underWarranty) => {
        setSelectedItems((prev) => ({
            ...prev,
            [inventoryId]: { ...prev[inventoryId], underWarranty },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const inventoryItems = Object.entries(selectedItems).map(([id, data]) => ({
            id,
            quantity: data.quantity,
            underWarranty: data.underWarranty,
        }));

        const payload = {
            ...formData,
            customerId,
            inventoryItem: inventoryItems,
        };

        try {
            await addJobCardForWorkshopId(workshopId, payload);
            handleClose();
        } catch (error) {
            console.error("Error creating job card:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${
                show ? "" : "hidden"
            }`}
            onClick={handleClose} // Close modal when clicking outside
        >
            <div
                className="bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 p-6 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
            >
                {/* Modal Header */}
                <div className="flex justify-between items-center border-b pb-4">
                    <h2 className="text-xl font-semibold text-blue-600 flex items-center">
                        <FaTools className="mr-2" />
                        Create Job Card
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        &times;
                    </button>
                </div>

                {/* Modal Body */}
                <div className="mt-4">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Customer and Vehicle Details */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaUser className="mr-2" />
                                Customer & Vehicle Details
                            </h5>
                            <hr className="my-2" />
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Customer ID
                                    </label>
                                    <div className="mt-1 flex rounded-md shadow-sm">
                                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                            <FaUser />
                                        </span>
                                        <input
                                            type="text"
                                            value={customerId}
                                            readOnly
                                            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-500"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Vehicle Details
                                    </label>
                                    <div className="mt-1 flex rounded-md shadow-sm">
                                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                            <FaCar />
                                        </span>
                                        <input
                                            type="text"
                                            name="vehicleNumber"
                                            value={formData.vehicleNumber}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Work Types */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaTools className="mr-2" />
                                Work Types
                            </h5>
                            <hr className="my-2" />
                            {workTypesLoading ? (
                                <div className="flex justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                workTypes.map((workType) => (
                                    <div key={workType.id} className="flex items-center mt-2">
                                        <input
                                            type="checkbox"
                                            id={`workType-${workType.id}`}
                                            checked={formData.workTypes.includes(workType.id)}
                                            onChange={() => handleWorkTypeChange(workType.id)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label
                                            htmlFor={`workType-${workType.id}`}
                                            className="ml-2 text-sm text-gray-700"
                                        >
                                            {`${workType.name} (₹${workType.price})`}
                                        </label>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Inventory Items */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaBoxOpen className="mr-2" />
                                Inventory Items
                            </h5>
                            <hr className="my-2" />
                            {inventoryOptions.map((item) => (
                                <div key={item.id} className="grid grid-cols-3 gap-4 items-center mb-2">
                                    <div>
                                        <input
                                            type="checkbox"
                                            id={`inventory-${item.id}`}
                                            checked={!!selectedItems[item.id]}
                                            onChange={(e) =>
                                                handleSelectChange(item.id, e.target.checked)
                                            }
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label
                                            htmlFor={`inventory-${item.id}`}
                                            className="ml-2 text-sm text-gray-700"
                                        >
                                            {item.name}
                                        </label>
                                    </div>
                                    <div>
                                        <input
                                            type="number"
                                            min="1"
                                            value={selectedItems[item.id]?.quantity || ""}
                                            onChange={(e) =>
                                                handleQuantityChange(
                                                    item.id,
                                                    parseInt(e.target.value, 10) || 1
                                                )
                                            }
                                            disabled={!selectedItems[item.id]}
                                            className="p-2 border rounded-md focus:ring-2 focus:ring-teal-500"
                                        />
                                    </div>
                                    <div>
                                        {item.warrantyPeriod > 0 && (
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`warranty-${item.id}`}
                                                    checked={selectedItems[item.id]?.underWarranty || false}
                                                    onChange={(e) =>
                                                        handleWarrantyChange(item.id, e.target.checked)
                                                    }
                                                    disabled={!selectedItems[item.id]}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                />
                                                <label
                                                    htmlFor={`warranty-${item.id}`}
                                                    className="ml-2 text-sm text-gray-700"
                                                >
                                                    Under Warranty
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Estimated Time */}
                        <div>
                            <h5 className="text-blue-600 font-semibold flex items-center">
                                <FaClock className="mr-2" />
                                Estimated Time
                            </h5>
                            <hr className="my-2" />
                            <div className="flex rounded-md shadow-sm">
                                <input
                                    type="number"
                                    name="estimatedTime"
                                    value={formData.estimatedTime || ""}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-500"
                                />
                                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500">
                                    Hours
                                </span>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Creating Job Card...
                                </div>
                            ) : (
                                "Create Job Card"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateJobCardModal;